import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { GoogleMap, LoadScript, DirectionsRenderer } from '@react-google-maps/api';
import { Autocomplete, TextField } from '@mui/material';
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Group,
  Inject,
  Page,
  Sort,
} from '@syncfusion/ej2-react-grids';
import useMapViewData from '../../CustomHooks/useMapView';

const containerStyle = {
  width: '100%',
  height: '70vh',
};

const defaultCenter = {
  lat: 53.638755,
  lng: 7.410353,
};

const RouteView = ({ open, setOpen, siteAssessmentID }) => {
  const { routeDetails, mapData, isLoading } = useMapViewData(siteAssessmentID);

  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(15);
  const [directionsResponse, setDirectionsResponse] = useState(null);

  // Coordinates
  const [entranceCoordinates, setEntranceCoordinates] = useState(null);
  const [activeRouteForSaving, setActiveRouteForSaving] = useState(null);

  const directionsRendererRef = useRef(null);
  const mapRef = useRef(null);

  const [gridKey, setGridKey] = useState(0);
  const [localRouteDetails, setLocalRouteDetails] = useState([]);

  // Calculate initial distances for all routes when entrance is selected
  useEffect(() => {
    const calculateInitialDistances = () => {
      if (!entranceCoordinates || !routeDetails || routeDetails.length === 0) {
        setLocalRouteDetails(routeDetails || []);
        return;
      }

      const updatedRoutes = routeDetails.map(route => {
        // If route already has a saved distance from DB, keep it
        if (route.distance && route.distance !== 'N/A' && route.polyLine) {
          return route;
        }

        // Calculate straight-line distance for routes without saved distance
        const destLat = route.destinationLatitude || route.latitude;
        const destLng = route.destinationLongitude || route.longitude;

        if (destLat && destLng && entranceCoordinates.latitude && entranceCoordinates.longitude) {
          const distance = haversineDistance(
            entranceCoordinates.latitude,
            entranceCoordinates.longitude,
            destLat,
            destLng,
          ).toFixed(2);

          return {
            ...route,
            distance: `${distance} km`,
          };
        }

        return route;
      });

      setLocalRouteDetails(updatedRoutes);
    };

    calculateInitialDistances();
  }, [routeDetails, entranceCoordinates]);

  function haversineDistance(lat1, lon1, lat2, lon2) {
    const toRad = deg => (deg * Math.PI) / 180;
    const R = 6371; // Radius of Earth in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  // Auto-select entrance from mapData
  useEffect(() => {
    if (open && mapData?.length > 0) {
      for (let i = 0; i < mapData.length; i++) {
        const item = mapData[i];
        if (item?.mapLabel?.toLowerCase().includes('entrance')) {
          setEntranceCoordinates(item);
          console.log('Auto-selected entrance:', item.mapLabel);
          break;
        }
      }
    }
  }, [open, mapData]);

  const handleClose = () => {
    setOpen(false);
    setEntranceCoordinates(null);
    setActiveRouteForSaving(null);
    setDirectionsResponse(null);
    setGridKey(0);
    setLocalRouteDetails([]);
  };

  const handleEntranceCoordinatesChange = newVal => {
    console.log('Entrance changed to:', newVal);
    setEntranceCoordinates(newVal);

    // If there's an active route, recalculate it with the new entrance
    if (activeRouteForSaving && newVal) {
      setDirectionsResponse(null);

      setTimeout(() => {
        const routeToRecalculate = {
          ...activeRouteForSaving,
          polyLine: null,
        };
        handleRouteButtonClick(routeToRecalculate);
      }, 100);
    }

    // Update map center to new entrance location
    if (newVal && mapRef.current) {
      mapRef.current.panTo({
        lat: newVal.latitude,
        lng: newVal.longitude,
      });
      mapRef.current.setZoom(14);
    }
  };

  const renderSavedRouteAsDirections = polylineString => {
    if (!window.google || !window.google.maps) return;

    try {
      const decodedPath = window.google.maps.geometry.encoding.decodePath(polylineString);

      if (decodedPath.length < 2) {
        console.warn('Polyline has too few points.');
        return;
      }

      const downsamplePath = (path, maxWaypoints = 15) => {
        const maxTotalPoints = maxWaypoints + 2;
        if (path.length <= maxTotalPoints) return path;

        const interval = Math.floor(path.length / (maxTotalPoints - 1));
        const downsampled = [path[0]];

        for (let i = interval; i < path.length - 1 && downsampled.length < maxTotalPoints - 1; i += interval) {
          downsampled.push(path[i]);
        }

        downsampled.push(path[path.length - 1]);
        return downsampled;
      };

      const simplifiedPath = downsamplePath(decodedPath, 15);
      const origin = simplifiedPath[0];
      const destination = simplifiedPath[simplifiedPath.length - 1];
      const waypoints = simplifiedPath.slice(1, -1).map(location => ({
        location,
        stopover: false,
      }));

      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin,
          destination,
          waypoints,
          travelMode: 'DRIVING',
          optimizeWaypoints: false,
        },
        (result, status) => {
          if (status === 'OK') {
            setDirectionsResponse(result);
          } else {
            console.error(`Directions request failed: ${status}`);
          }
        },
      );
    } catch (error) {
      console.error('Error decoding or rendering route:', error);
    }
  };

  const rowDataBound = args => {
    const isPolylinePresent = args.data.polyLine;
    if (isPolylinePresent) {
      args.row.style.backgroundColor = '#d4edda'; // light green
    }
  };

  const handleRouteButtonClick = route => {
    if (!entranceCoordinates) {
      alert('Select Entrance');
      return;
    }
    if (
      !entranceCoordinates.latitude ||
      !entranceCoordinates.longitude ||
      typeof entranceCoordinates.latitude !== 'number' ||
      typeof entranceCoordinates.longitude !== 'number'
    ) {
      console.error('Invalid entrance coordinates:', entranceCoordinates);
      alert('Invalid entrance coordinates. Please select a valid entrance.');
      return;
    }

    const destLat = route.destinationLatitude || route.latitude;
    const destLng = route.destinationLongitude || route.longitude;

    if (!destLat || !destLng || typeof destLat !== 'number' || typeof destLng !== 'number') {
      console.error('Invalid destination coordinates:', route);
      alert('Invalid destination coordinates for this route.');
      return;
    }

    setActiveRouteForSaving(route);
    setGridKey(prev => prev + 1);

    if (route.polyLine) {
      renderSavedRouteAsDirections(route.polyLine);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: {
          lat: Number(entranceCoordinates.latitude),
          lng: Number(entranceCoordinates.longitude),
        },
        destination: {
          lat: Number(destLat),
          lng: Number(destLng),
        },
        travelMode: 'DRIVING',
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
          setGridKey(prev => prev + 1);
        } else {
          console.error(`Directions request failed due to ${status}`);
          alert(`Unable to calculate route: ${status}`);
          setGridKey(prev => prev + 1);
        }
      },
    );
  };

  const getRouteTemplate = rowData => {
    const isSelected = rowData?.destination === activeRouteForSaving?.destination;

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Button
          color={isSelected ? 'secondary' : 'primary'}
          variant={isSelected ? 'contained' : 'text'}
          size="small"
          onClick={() => handleRouteButtonClick(rowData)}>
          {rowData.destination}
        </Button>
      </div>
    );
  };

  const filterSettings = {
    type: 'Excel',
  };

  const handleMapLoad = map => {
    mapRef.current = map;

    if (entranceCoordinates) {
      map.panTo({ lat: entranceCoordinates.latitude, lng: entranceCoordinates.longitude });
      map.setZoom(12);
    } else {
      map.panTo(center);
      map.setZoom(zoom);
    }
  };

  return (
    <Dialog maxWidth="xl" open={open} onClose={handleClose} disableEscapeKeyDown={true} disableAutoFocus>
      <DialogTitle style={{ paddingBlock: '.25rem' }}>
        <Typography style={{ fontSize: '1.5rem' }} component="div" variant="subtitle2">
          Route View
        </Typography>
      </DialogTitle>

      <DialogContent style={{ width: '95vw', height: '80vh' }}>
        <Box
          noValidate
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            m: 'auto',
            width: '100%',
            gap: '1rem',
          }}>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'row-reverse' }}>
            {/* Map Section */}
            <div style={{ position: 'relative', height: '100%', flex: 1 }}>
              <LoadScript libraries={['geometry']} googleMapsApiKey="AIzaSyA94J1dEXShLTlOZmIZbmOT31Qyo2g-2OU">
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  zoom={zoom}
                  mapTypeId="hybrid"
                  options={{
                    mapTypeId: 'hybrid',
                    streetViewControl: true,
                    mapTypeControl: true,
                    rotateControl: true,
                  }}
                  onLoad={handleMapLoad}>
                  {directionsResponse && (
                    <DirectionsRenderer
                      options={{
                        draggable: false, // Read-only
                      }}
                      directions={directionsResponse}
                      onLoad={directionsRenderer => {
                        directionsRendererRef.current = directionsRenderer;
                      }}
                    />
                  )}
                </GoogleMap>
              </LoadScript>
            </div>

            {/* Grid Section */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                flex: 0.5,
                display: 'flex',
                justifyContent: 'start',
                gap: '0.5rem',
                flexDirection: 'column',
                alignItems: 'start',
                paddingTop: '0.35rem',
              }}>
              <Autocomplete
                size="small"
                disablePortal
                name="Entrance"
                value={entranceCoordinates}
                options={mapData || []}
                onChange={(event, newValue) => handleEntranceCoordinatesChange(newValue)}
                sx={{ fontSize: '0.8rem' }}
                isOptionEqualToValue={(option, value) => option?.mapLabel === value?.mapLabel}
                getOptionLabel={option => option?.mapLabel || ''}
                renderInput={params => <TextField label={'Entrance'} {...params} size="small" fullWidth />}
                fullWidth
              />

              <div style={{ height: 'calc(100vh - 260px)', width: '100%' }}>
                <GridComponent
                  dataSource={localRouteDetails}
                  filterSettings={filterSettings}
                  allowSorting={true}
                  allowTextWrap={true}
                  allowResizing={true}
                  allowMultiSorting={true}
                  allowReordering={true}
                  allowFiltering={true}
                  style={{
                    minHeight: 'auto !important',
                  }}
                  height="100%"
                  rowDataBound={rowDataBound}
                  key={`grid-${gridKey}`}>
                  <ColumnsDirective>
                    <ColumnDirective field="destination" headerText="Destination" width="100" template={getRouteTemplate} />
                    <ColumnDirective field="distance" headerText="Distance" width="100" />
                  </ColumnsDirective>
                  <Inject services={[Page, Sort, Filter, Group]} />
                </GridComponent>
              </div>
            </div>
          </div>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouteView;
